import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  FormErrorMessage,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const CreatePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    path: '',
    description: '',
    site_id: '',
    layout_id: '',
    is_home: false,
    meta_title: '',
    meta_description: '',
    status: 'draft'
  });
  const [sites, setSites] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [sitesLoading, setSitesLoading] = useState(true);

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (tenantId) {
      fetchSites();
      fetchLayouts();
    }
  }, [tenantId]);

  const fetchSites = async () => {
    try {
      setSitesLoading(true);
      const response = await apiClient(`/sites/${tenantId}`);
      setSites(Array.isArray(response) ? response : []);
    } catch (error) {
      setSites([]);
    } finally {
      setSitesLoading(false);
    }
  };

  const fetchLayouts = async () => {
    try {
      const response = await apiClient(`/component-layouts/${tenantId}`);
      console.log('Layout response', response)
      setLayouts(response.data || []);
    } catch (error) {
      setLayouts([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Page name is required';
    if (!formData.path.trim()) newErrors.path = 'URL path is required';
    else if (!formData.path.startsWith('/')) newErrors.path = 'URL path must start with /';
    if (!formData.site_id) newErrors.site_id = 'Please select a site';
    if (!formData.layout_id) newErrors.layout_id = 'Please select a layout';
    if (formData.path && !formData.path.match(/^\/[a-z0-9\-\/]*$/)) {
      newErrors.path = 'URL path can only contain lowercase letters, numbers, hyphens, and forward slashes';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await apiClient(`/pages/${tenantId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, tenant_id: tenantId })
      });
      if (response.error) throw new Error(response.error);
      toast({ title: 'Page created successfully', description: `Page "${formData.name}" has been created`, status: 'success', duration: 3000, isClosable: true });
      if (response.id) navigate(`/pages/${response.id}/builder`);
      else navigate('/pages');
    } catch (error) {
      toast({ title: 'Error creating page', description: error.message || 'Failed to create page', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const generatepath = (name) => {
    if (!name) return '';
    return '/' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    const updates = { name };
    if (!formData.path || formData.path === generatepath(formData.name)) {
      updates.path = generatepath(name);
    }
    setFormData({ ...formData, ...updates });
    if (errors.name) setErrors({ ...errors, name: null });
  };

  const handlePathChange = (e) => {
    let path = e.target.value;
    if (path && !path.startsWith('/')) path = '/' + path;
    setFormData({ ...formData, path });
    if (errors.path) setErrors({ ...errors, path: null });
  };

  const ComponentPreview = ({ component }) => {
    if (!component) return null;
    const { type, props = {} } = component;

    switch (type) {
      case 'hero-section':
        return (
          <Box p={4} bg="blue.50" borderRadius="md">
            <Heading size="lg">{props.title || 'Hero Title'}</Heading>
            <Text fontSize="md">{props.subtitle || 'Hero Subtitle'}</Text>
            <Button colorScheme="blue" mt={4}>{props.cta_text || 'Get Started'}</Button>
          </Box>
        );
      case 'text-block':
        return (
          <Box p={4} bg="gray.50" borderRadius="md">
            <Text fontSize={props.font_size || 'md'}>{props.content || 'Text Block'}</Text>
          </Box>
        );
      case 'image-gallery':
        return (
          <Box p={4} bg="gray.100" borderRadius="md">
            <Text>Image Gallery ({Array.isArray(props.images) ? props.images.length : 0} images)</Text>
          </Box>
        );
      // Add more cases for your custom components
      default:
        return (
          <Box p={4} bg="gray.50" borderRadius="md">
            <Text fontWeight="bold">{type}</Text>
            <pre style={{ fontSize: 12, color: '#666' }}>{JSON.stringify(props, null, 2)}</pre>
          </Box>
        );
    }
  };

  const getLayoutName = (layoutId) => {
    const layout = layouts.find(l => l.id?.toString() === layoutId?.toString());
    return layout ? layout.name : 'Unknown Layout';
  };

  if (!tenantId) {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="error"><AlertIcon />No tenant ID found. Please log in again.</Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/pages">Pages</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Create Page</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Box>
              <Heading size="lg" mb={2}>Create New Page</Heading>
              <Text color="gray.500">
                Create a new page for your website
              </Text>
            </Box>
            <HStack>
              <Button 
                variant="outline" 
                onClick={() => navigate('/pages')}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={loading}
                loadingText="Creating..."
                isDisabled={sitesLoading || sites.length === 0}
              >
                Create Page
              </Button>
            </HStack>
          </HStack>

          {sites.length === 0 && !sitesLoading && (
            <Alert status="warning">
              <AlertIcon />
              No sites available. You need to create a site first before creating pages.
            </Alert>
          )}

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Basic Information</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <FormControl isRequired isInvalid={!!errors.name}>
                  <FormLabel>Page Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder="About Us"
                    isDisabled={loading}
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.path}>
                  <FormLabel>URL Path</FormLabel>
                  <Input
                    value={formData.path}
                    onChange={handlePathChange}
                    placeholder="/about-us"
                    isDisabled={loading}
                  />
                  <FormErrorMessage>{errors.path}</FormErrorMessage>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    The URL where this page will be accessible
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the page..."
                    rows={3}
                    isDisabled={loading}
                  />
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.site_id}>
                  <FormLabel>Site</FormLabel>
                  <Select
                    value={formData.site_id}
                    onChange={(e) => {
                      setFormData({ ...formData, site_id: e.target.value });
                      if (errors.site_id) {
                        setErrors({ ...errors, site_id: null });
                      }
                    }}
                    placeholder={sitesLoading ? "Loading sites..." : "Select a site"}
                    isDisabled={loading || sitesLoading}
                  >
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name} ({site.domain})
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.site_id}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.layout_id}>
                  <FormLabel>Layout</FormLabel>
                  <Select
                    value={formData.layout_id}
                    onChange={(e) => setFormData({ ...formData, layout_id: e.target.value })}
                    placeholder="Select a layout"
                    isDisabled={loading}
                  >
                    {layouts.map((layout) => (
                      <option key={layout.id} value={layout.id}>{layout.name}</option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.layout_id}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    isDisabled={loading}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </Select>
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">
                    Set as Homepage
                  </FormLabel>
                  <Switch
                    isChecked={formData.is_home}
                    onChange={(e) => setFormData({ ...formData, is_home: e.target.checked })}
                    isDisabled={loading}
                  />
                  <Text fontSize="sm" color="gray.500" ml={3}>
                    This will be the default page for the site
                  </Text>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">SEO Settings</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Meta Title</FormLabel>
                  <Input
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    placeholder={formData.name || "Page title for search engines"}
                    isDisabled={loading}
                  />
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Recommended: 50-60 characters
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Meta Description</FormLabel>
                  <Textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="Brief description for search engines..."
                    rows={3}
                    isDisabled={loading}
                  />
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Recommended: 150-160 characters
                  </Text>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </form>
    </Container>
  );
};

export default CreatePage;